import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from 'src/app/shared/services';
import { Store } from '@ngrx/store';
import { lastValueFrom, takeWhile } from 'rxjs';

import {
  WizardModel,
  WizardStepModel,
  WizardStepStatusEnum,
  StepVisibilityEnum,
  WizardTypeEnum,
} from 'src/app/models/wizard';
import { GlobalWizardSettings } from '../../portal/dto/portal.input';
import { ProductItem, ProductWizardData } from '../models/product';
import { CourseCreationInput, CurriculumAiData } from '../models/course';
import { Offer_Payment_Type_Enum } from '../../offer/models/offer';
import { SubWizardStepEnum } from '../models/product-create.model';
import {
  createNewProductActions,
  ProductsActions,
} from 'src/app/shared/store/_features/products/products.actions';
import {
  selectCurrentProductIsFirstCourse,
  selectProductsState,
} from 'src/app/shared/store/_features/products/products.selector';
import {
  CurriculumSeedProductActions,
  GenerateCourseAiHierarchyActions,
  GenerateLessonAiContentActions,
} from 'src/app/shared/store/_features/products/course/course.actions';
import { isCurrentCourseHeirarchyProcessing } from 'src/app/shared/store/_features/products/course/course.selector';
import { SystemProcessLoadingStatus } from 'src/app/models/global-classes';
import { ProductTypeStatusEnum } from '../../blog/models/blog';
import { ProductType } from '../models/product.shared';

@Injectable({ providedIn: 'root' })
export class ProductCreateService {
  courseBuilderProcessing: {
    generallyLoading: boolean;
    status: string;
    loadingMessage: string;
    error?: { processType: SystemProcessLoadingStatus; error: any };
  } = {
    generallyLoading: false,
    status: 'none',
    loadingMessage: 'Updating',
  };
  // ──────────────────────────────────────────────
  // PRODUCT TYPE CONFIGURATION
  // ──────────────────────────────────────────────
  readonly productTypesData: Record<string, ProductType> = {
    manual: {
      id: 1,
      name: 'Manual Mode',
      enum: 'manual',
      iconName: 'keyboard',
      status: ProductTypeStatusEnum.active,
      description:
        'Enter all details manually with full control over every aspect of your course.',
      subDescription:
        'Perfect for experienced creators who know exactly what they want.',
    },
    ai: {
      id: 2,
      name: 'AI-Assisted Mode',
      enum: 'ai',
      iconName: 'ai',
      status: ProductTypeStatusEnum.active,
      isRecommended: true,
      description:
        'Save time with smart AI-generated ideas and suggestions throughout the creation process.',
      subDescription:
        'Ideal for creators who want guidance and inspiration while maintaining control.',
    },
  };

  // ──────────────────────────────────────────────
  // STATE VARIABLES
  // ──────────────────────────────────────────────
  public loadingCreate = false;
  public wizardModel!: WizardModel;

  public wizardSettings: GlobalWizardSettings & {
    identitySubWizardCurrentStep?: SubWizardStepEnum;
  } = this.getDefaultWizardSettings();

  public wizardData: ProductWizardData = this.getDefaultWizardData();

  public countForFinishing = {
    count: null as number | null,
    counter: null as any,
  };

  createdProduct: ProductItem | null = null;

  isFirstCourse: boolean = false;

  // ──────────────────────────────────────────────
  // DEPENDENCY INJECTION
  // ──────────────────────────────────────────────
  constructor(
    private router: Router,
    private toastr: ToastrService,
    private api: ApiService,
    private store: Store,
  ) {
    this.store.dispatch(ProductsActions.load());
    this.store
      .select(selectCurrentProductIsFirstCourse)
      .subscribe((isFirstCourse) => {
        this.isFirstCourse = isFirstCourse;
      });
  }

  // ──────────────────────────────────────────────
  // INITIALIZATION
  // ──────────────────────────────────────────────
  public initializeWizardProductCreate(isGift = false): void {
    if (isGift) {
      this.wizardData.productMode = 'gift';
      this.wizardData.productType = this.productTypesData.ai;
      this.initializeCourseProductCreate(isGift, true);
    }

    this.wizardSettings.finalStepLabel = isGift
      ? 'Create Gift Product'
      : 'Create My Product';

    this.wizardModel = new WizardModel(
      this.getSteps(isGift ? 'ready' : 'init', isGift, this.isFirstCourse),
      0,
      WizardTypeEnum.product,
      this.backAction,
      this.onProductCreateDone,
    );
    // setTimeout(() => {
    //   this.wizardModel.navigateByEnum('final');
    // }, 2000);
  }

  public async initializeCourseProductCreate(
    isGift = false,
    isFirstCourse: boolean = false,
  ): Promise<void> {
    this.wizardSettings.finalStepLabel = isGift
      ? 'Create Gift Course'
      : 'Create Course';
    this.initCourseInput(isGift);
    if (!isGift) {
      this.wizardModel?.setSteps(this.getSteps('ready', isGift, isFirstCourse));
    }
  }

  private initCourseInput(
    isGift: boolean = this.wizardData?.productMode == 'gift',
  ): void {
    this.wizardData.courseInput = {
      name: '',
      shortDescription: '',
      description: '',
      isGift,
      payment_type: {
        name: Offer_Payment_Type_Enum.free,
        price: null,
        isPaymentConfirmed: false,
      },
      courseMeta: {
        instructors: [],
        category: [],
        difficulty: '',
        duration: '',
        language: 'English',
      },
      aiState: {
        aiSeed: '',
        aiData: { audience: '', goals: '', idea: '' },
        curriculumAiData: {
          moduleCount: isGift ? 3 : 6,
          lessonsPerModule: 4,
          includeAssessment: false,
        },
      },
      finalOptions: {
        craftLanding: true,
        craftLessons: isGift,
        autoPublishFree: isGift,
        showAsGift: isGift,
        showAsFeatured: false,
      },
    };

    //     this.wizardData.courseInput = {
    //       name: 'Mastering Frontend Development',
    //       shortDescription: 'Learn how to build stunning, responsive web apps from scratch.',
    //       description: `This course covers everything from HTML and CSS fundamentals
    // to advanced React and Next.js concepts. You’ll learn how to architect scalable
    // frontend systems, optimize performance, and deliver professional-quality projects.`,
    //       isGift: false,
    //       payment_type: { name: Offer_Payment_Type_Enum.free, price: null },
    //       courseMeta: {
    //         instructors: [],
    //         category: ['Web Development', 'Frontend'],
    //         difficulty: 'Intermediate',
    //         duration: '8 hours',
    //         language: 'English',
    //       },
    //       aiState: {
    //         aiSeed: 'frontend-dev-ai-seed-001',
    //         aiData: {
    //           audience: 'Aspiring frontend developers and web designers',
    //           goals: 'Build production-ready React apps and deploy them efficiently',
    //           idea: 'A hands-on course guiding students through building a modern web app using React, TailwindCSS, and Next.js',
    //         },
    //       },
    //       finalOptions: {
    //         craftLanding: true,
    //         craftLessons: isGift,
    //         autoPublishFree: isGift,
    //         showAsGift: isGift,
    //         showAsFeatured: false,
    //       },
    //     };
  }

  // ──────────────────────────────────────────────
  // RESET HELPERS
  // ──────────────────────────────────────────────
  public resetWizard(): void {
    if (this.countForFinishing.counter)
      clearInterval(this.countForFinishing.counter);
    this.resetWizardValues();
  }

  private resetWizardValues(): void {
    this.countForFinishing = { count: null, counter: null };
    this.wizardSettings = this.getDefaultWizardSettings();
    this.resetTheAi();
    this.wizardData = this.getDefaultWizardData();
    this.wizardModel?.resetWizard();
  }

  private resetTheAi(): void {
    // TODO: implement AI reset logic
  }

  // ──────────────────────────────────────────────
  // WIZARD NAVIGATION
  // ──────────────────────────────────────────────
  private goTo(step: string): void {
    const basePath = ['dashboard', 'product', 'create'];
    const mode = this.wizardData.productMode === 'gift' ? 'gift' : null;
    const path = mode ? [...basePath, mode, step] : [...basePath, step];
    this.router.navigate(path);
  }

  goToSelectChooseProductType = () => this.goTo('step1');
  goToProductBrandingIdentity = () => this.goTo('product-branding-identity');
  goToProductIdea = () => this.goTo('product-idea');
  goToProductIdeationGenerate = () => this.goTo('product-idea-generate');
  goToProductMiniIdeas = () => this.goTo('product-mini-ideas');
  goToProductMeta = () => this.goTo('product-meta');
  goToProductImage = () => this.goTo('product-image');
  goToProductOffer = () => this.goTo('product-offer');
  goToProductCurriculum = () => this.goTo('product-curriculum');
  goToSummary = () => this.goTo('summary');
  goToCompletion = () => this.goTo('completion');

  backAction = () => this.router.navigate(['dashboard', 'product']);

  // ──────────────────────────────────────────────
  // VALIDATORS
  // ──────────────────────────────────────────────
  selectProductTypeValidator = () => !!this.wizardData?.productType;
  courseTitleValidator = () => !!this.wizardData?.courseInput?.name;
  courseIdeaValidator = () => !!this.wizardData?.courseInput?.aiState?.aiSeed;
  courseIdeationGenerateValidator = () =>
    !!this.wizardData?.courseInput?.aiState?.aiData?.idea &&
    !!this.wizardData?.courseInput?.aiState?.aiData?.audience &&
    !!this.wizardData?.courseInput?.aiState?.aiData?.goals;
  courseDescriptionValidator = () => {
    const meta = this.wizardData?.courseInput?.courseMeta;
    return !!(
      this.wizardData?.courseInput?.shortDescription && // Keep shortDescription check? Wait, the user said "Details Step" but the validator is named courseDescriptionValidator. Step 5 uses enumTag 'description' and calls this validator. The component inputs meta data mostly.
      // Actually, step 4 "Craft Your Course Identity" handled shortDescription?
      // Step 4 (Identity) uses `courseTitleValidator` (which checks name). Wait.
      // Step 4 (Identity) has enumTag 'title' and validate `this.courseTitleValidator`.
      // Step 4's validator currently checks `name`.
      // But `ProductBrandingIdentityWizardStepComponent` handles Title, Subtitle, Description.
      // `ProductMetaDataWizardStepComponent` handles Instructors, Category, Difficulty, Duration, Language.
      // The service definition for Step 5 (Course Details) uses `courseDescriptionValidator`.
      // The current implementation of `courseDescriptionValidator` checks `shortDescription`. This seems wrong or outdated if Step 5 is about Meta Data.
      // I will assume Step 5 should strictly validate the Meta Data fields as requested, and possibly shortDescription if it's considered part of "Description & Details".
      // However, the user request specifically lists: Instructor, Category, Difficulty, Language, Duration.
      // I will focus on those. I'll keep shortDescription check if safe, but primarily add the others.
      // Actually, shortDescription (subtitle) is step 4. So step 5 shouldn't fail on it if it was validated in step 4.
      // I will update it to check the META fields.
      meta?.instructors?.length &&
      meta?.category?.length &&
      meta?.difficulty &&
      meta?.duration &&
      meta?.language
    );
  };
  courseCurriculumValidator = () => {
    const c = this.wizardData?.courseInput?.aiState?.curriculumAiData;
    return !!(c?.moduleCount && c?.lessonsPerModule);
  };
  courseImageValidator = () => !!this.wizardData?.courseInput?.image_id || true;
  courseOfferValidator = () => {
    const payment = this.wizardData?.courseInput?.payment_type;
    if (
      payment?.name === Offer_Payment_Type_Enum.one_time &&
      payment?.price?.value <= 0
    ) {
      return false;
    }
    return true;
  };

  // ──────────────────────────────────────────────
  // CREATION FLOW
  // ──────────────────────────────────────────────
  createProduct = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      this.store.dispatch(
        createNewProductActions.create({
          createPayload: {
            // except finalOptions from the object this.wizardData.courseInput
            ...this.wizardData.courseInput,
            image: undefined,
            finalOptions: undefined,
            payment_type: this.wizardData.courseInput.finalOptions
              .autoPublishFree
              ? {
                  name: Offer_Payment_Type_Enum.free,
                  price: null,
                  isPaymentConfirmed: true,
                }
              : {
                  name: Offer_Payment_Type_Enum.free,
                  price: null,
                  isPaymentConfirmed: false,
                },
            isGift:
              this.wizardData.courseInput.finalOptions.autoPublishFree &&
              this.wizardData.courseInput.finalOptions.showAsGift,
            isFeatured: this.wizardData.courseInput.finalOptions.showAsFeatured,
            instructors: this.wizardData.courseInput.instructors,
          },
        }),
      );

      this.loadingCreate = true;

      this.store
        .select(selectProductsState)
        .pipe(
          takeWhile(
            (s) =>
              s?.createNewProductProcess.status !== 'done' &&
              s?.createNewProductProcess.status !== 'error',
            true,
          ),
        )
        .subscribe((state) => {
          switch (state?.createNewProductProcess.status) {
            case 'done':
              this.createdProduct =
                state.createNewProductProcess.lastCreatedProduct;
              resolve();
              break;
            case 'error':
              reject(state.createNewProductProcess.error);
              break;
          }
        });
    });
  };

  generateCourseCurriculum = (
    id: number = this.createdProduct?.id,
    curriculumAiData: CurriculumAiData = this.wizardData.courseInput.aiState
      .curriculumAiData,
  ) => {
    return new Promise((resolve, reject) => {
      this.store.dispatch(
        GenerateCourseAiHierarchyActions.generate({
          courseId: id,
          courseData: {
            apply: true,
            aiState: {
              curriculumAiData,
            },
          },
        }),
      );
      this.store
        .select(isCurrentCourseHeirarchyProcessing)
        .pipe(
          takeWhile((s) => s?.status !== 'done' && s?.status !== 'error', true),
        )
        .subscribe((value) => {
          this.courseBuilderProcessing = {
            generallyLoading:
              value.status != 'done' &&
              value.status != 'none' &&
              value.status != 'error',
            status: value.status,
            loadingMessage: value.loadingMessage,
            error:
              value.status === 'error'
                ? {
                    processType: value?.error?.processType,
                    error: value?.error?.error,
                  }
                : null,
          };
          if (value.status === 'done') {
            resolve(0);
          }
          if (value.status === 'error') {
            reject(value.error);
          }
        });
    });
  };

  generateCourseLessonsContent = (id: number = this.createdProduct?.id) => {
    return new Promise((resolve, reject) => {
      this.store.dispatch(
        GenerateLessonAiContentActions.generateAll({
          courseId: id,
        }),
      );
      this.store
        .select(isCurrentCourseHeirarchyProcessing)
        .pipe(
          takeWhile((s) => s?.status !== 'done' && s?.status !== 'error', true),
        )
        .subscribe((value) => {
          this.courseBuilderProcessing = {
            generallyLoading:
              value.status != 'done' &&
              value.status != 'none' &&
              value.status != 'error',
            status: value.status,
            loadingMessage: value.loadingMessage,
            error:
              value.status === 'error'
                ? {
                    processType: value?.error?.processType,
                    error: value?.error?.error,
                  }
                : null,
          };
          if (value.status === 'done') {
            resolve(0);
          }
          if (value.status === 'error') {
            reject(value.error);
          }
        });
    });
  };

  onProductCreateDone = (): void => {
    this.startAnimationAndRedirect(() => {
      this.router
        .navigate([
          'dashboard',
          'product',
          this.createdProduct?.id,
          'course',
          'builder',
          'outline',
        ])
        .then(() => {
          this.toastr.success('Product Created Successfully', 'Done!');
          this.resetWizard();
          this.loadingCreate = false;
          this.scrollTo(`productItem-${this.createdProduct?.id}`);
        });
    });
  };

  onProductCreateError = (errorMessage: string): void => {
    this.startAnimationAndRedirect(() => {
      this.router.navigate(['dashboard', 'product']).then(() => {
        this.toastr.error(errorMessage, 'Error!');
        this.resetWizard();
        this.loadingCreate = false;
      });
    });
  };

  async createNewProduct(courseInput: CourseCreationInput) {
    return await lastValueFrom(this.api.createCourse(courseInput));
  }

  // ──────────────────────────────────────────────
  // HELPERS
  // ──────────────────────────────────────────────
  setWizardNextTitle(text: string) {
    this.wizardModel.currentStep.step.nextButtonLabel = text;
  }

  startAnimationAndRedirect = (callback: () => void | Promise<void>) => {
    this.countForFinishing.count = 5;
    this.countForFinishing.counter = setInterval(() => {
      this.countForFinishing.count!--;
      if (this.countForFinishing.count! <= 0) callback();
    }, 1000);
  };

  private scrollTo(
    elementID: string,
    position: ScrollLogicalPosition = 'nearest',
  ) {
    document.getElementById(elementID)?.scrollIntoView({
      behavior: 'smooth',
      block: position,
    });
  }

  private getDefaultWizardSettings(): GlobalWizardSettings {
    return {
      portalType: null,
      summaryAnimation: null,
      finalStepLabel: 'Create',
    };
  }

  private getDefaultWizardData(): ProductWizardData {
    return { productType: null, productMode: 'regular', temp: {} };
  }

  // ──────────────────────────────────────────────
  // STEPS
  // ──────────────────────────────────────────────
  getSteps(
    type: 'init' | 'ready' = 'init',
    isGift = false,
    enableMiniCourseIdeasStep: boolean = false,
  ): WizardStepModel[] {
    if (type === 'init') {
      return [
        {
          id: 1,
          title: 'Product Type',
          description: 'Construct your product',
          innerContent: { title: 'Select Your Course Creation Mode' },
          status: WizardStepStatusEnum.none,
          enumTag: 'template',
          visibility: StepVisibilityEnum.visible,
          isValid: false,
          hideButtons: { back: false, next: false },
          hideSteps: true,
          navigationAction: this.goToSelectChooseProductType,
          submissionCustomAction: this.onTypeSubmission,
          validate: this.selectProductTypeValidator,
          nextButtonLabel: 'Choose to continue',
        },
        {
          id: 2,
          title: 'Variant Steps',
          description: 'Depends on type',
          status: WizardStepStatusEnum.dummy,
          visibility: StepVisibilityEnum.visible,
          isValid: true,
          hideButtons: { back: false, next: false },
          navigationAction: this.goToProductBrandingIdentity,
        },
      ];
    }

    return [
      ...(isGift
        ? []
        : [
            {
              id: 1,
              title: 'Product Type',
              description: 'Construct your product',
              innerContent: { title: 'Select Your Course Creation Mode' },
              status: WizardStepStatusEnum.active_done,
              enumTag: 'template',
              canNavigateByClick: true,
              visibility: StepVisibilityEnum.hidden,
              isValid: false,
              hideButtons: { back: false, next: false },
              hideSteps: false,
              navigationAction: this.goToSelectChooseProductType,
              submissionCustomAction: this.onTypeSubmission,
              validate: this.selectProductTypeValidator,
              nextButtonLabel: 'Continue',
            },
          ]),
      ...(enableMiniCourseIdeasStep
        ? [
            {
              id: 2,
              title: 'Mini Ideas',
              description: 'Name your course',
              enumTag: 'idea',
              innerContent: { title: 'Describe Your Course Idea' },
              status: WizardStepStatusEnum.none,
              visibility: StepVisibilityEnum.visible,
              isValid: false,
              validate: this.courseIdeaValidator,
              nextButtonLabel: 'Continue',
              hideButtons: { back: false, next: false },
              navigationAction: this.goToProductMiniIdeas,
            },
          ]
        : [
            {
              id: 2,
              title: 'Course Idea',
              description: 'Name your course',
              enumTag: 'idea',
              innerContent: { title: 'Describe Your Course Idea' },
              status: WizardStepStatusEnum.none,
              visibility: StepVisibilityEnum.visible,
              isValid: false,
              validate: this.courseIdeaValidator,
              nextButtonLabel: 'Start Ideation',
              hideButtons: { back: false, next: false },
              navigationAction: this.goToProductIdea,
            },
          ]),

      {
        id: 3,
        title: 'Refine Your Course Foundations',
        description: 'Name your course',
        enumTag: 'idea',
        innerContent: { title: 'Refine Your Course Foundations' },
        status: WizardStepStatusEnum.none,
        visibility: StepVisibilityEnum.hidden,
        isValid: false,
        validate: this.courseIdeationGenerateValidator,
        hideButtons: { back: false, next: false },
        navigationAction: this.goToProductIdeationGenerate,
      },
      {
        id: 4,
        title: 'Craft Your Course Identity',
        enumTag: 'title',
        description: 'Name your course',
        innerContent: { title: 'Craft Your Course Identity' },
        status: WizardStepStatusEnum.none,
        visibility: StepVisibilityEnum.visible,
        isValid: false,
        validate: this.courseTitleValidator,
        hideButtons: { back: true, next: true },
        navigationAction: this.goToProductBrandingIdentity,
      },
      {
        id: 5,
        title: 'Course Details',
        enumTag: 'description',
        description: 'Description & Details',
        innerContent: { title: 'Define Course Details' },
        status: WizardStepStatusEnum.none,
        visibility: StepVisibilityEnum.visible,
        isValid: false,
        validate: this.courseDescriptionValidator,
        hideButtons: { back: false, next: false },
        navigationAction: this.goToProductMeta,
      },
      {
        id: 6,
        title: 'Course Image',
        description: 'Visual Representation',
        innerContent: { title: 'Create Your Course Cover Image' },
        status: WizardStepStatusEnum.none,
        visibility: StepVisibilityEnum.visible,
        isValid: false,
        validate: this.courseImageValidator,
        hideButtons: { back: false, next: false },
        navigationAction: this.goToProductImage,
      },
      {
        id: 8,
        title: 'Curriculum Structure',
        description: 'Product Summary',
        innerContent: { title: 'Outline Your Course Curriculum' },
        status: WizardStepStatusEnum.none,
        visibility: StepVisibilityEnum.visible,
        isValid: true,
        validate: this.courseCurriculumValidator,
        hideButtons: { back: false, next: false },
        navigationAction: this.goToProductCurriculum,
        nextButtonLabel: 'Save & Continue',
      },
      {
        id: 9,
        title: 'Final Options',
        description: 'Product Summary',
        enumTag: 'final',
        innerContent: {
          title: 'Finalize Your Course',
          description:
            'Review your final options and let the Learmo AI assist with crafting, publishing, and showcasing your course.',
        },
        status: WizardStepStatusEnum.none,
        visibility: StepVisibilityEnum.visible,
        isValid: true,
        validate: () => true,
        hideButtons: { back: false, next: false },
        navigationAction: this.goToSummary,
        nextButtonLabel: 'Finalize & Create Course',
      },
      {
        id: 10,
        title: 'Completion',
        description: 'Product Summary',
        enumTag: 'completion',
        innerContent: {
          title: 'Initializing your Course',
          description: 'Creating your Course, please wait…',
        },
        status: WizardStepStatusEnum.none,
        visibility: StepVisibilityEnum.visible,
        isValid: true,
        validate: () => true,
        hideButtons: { back: true, next: true },
        navigationAction: this.goToCompletion,
        nextButtonLabel: 'Finalize & Create Course',
      },
    ];
  }

  // ──────────────────────────────────────────────
  // TYPE SUBMISSION
  // ──────────────────────────────────────────────
  onTypeSubmission = (): void => {
    if (
      this.wizardData?.productType?.enum === 'manual' &&
      !this.wizardData.courseInput
    ) {
      this.initCourseInput();
    }
  };
}
